import { lazy } from "react";
import { RouteObject, Outlet, Navigate, useParams } from "react-router-dom";

const HomePage = lazy(() => import("../pages/home/page"));
const ContactPage = lazy(() => import("../pages/contact/page"));
const AboutPage = lazy(() => import("../pages/about/page"));
const OurStoryPage = lazy(() => import("../pages/about/our-story/page"));
const MissionValuesPage = lazy(() => import("../pages/about/mission-values/page"));
const WhyCendienPage = lazy(() => import("../pages/about/why-cendien/page"));
const CertificationsAboutPage = lazy(() => import("../pages/about/certifications/page"));
const DallasCoveragePage = lazy(() => import("../pages/about/dallas-coverage/page"));
const ServicesPage = lazy(() => import("../pages/services/page"));
const StaffingPage = lazy(() => import("../pages/services/staffing/page"));
const ERPPage = lazy(() => import("../pages/services/erp/page"));
const ManagedPage = lazy(() => import("../pages/services/managed/page"));
const ServiceDeskPage = lazy(() => import("../pages/services/service-desk/page"));
const AIPage = lazy(() => import("../pages/services/ai/page"));
const AIDataPage = lazy(() => import("../pages/services/ai-data/page"));
const HealthcareITPage = lazy(() => import("../pages/services/healthcare-it/page"));
const CloudPage = lazy(() => import("../pages/services/cloud/page"));

// ERP Sub-service Pages
const ERPImplementationPage = lazy(() => import("../pages/services/erp/implementation/page"));
const ERPManagedServicesPage = lazy(() => import("../pages/services/erp/managed-services/page"));
const ERPInforSupportPage = lazy(() => import("../pages/services/erp/infor-support/page"));
const ERPWorkdaySupportPage = lazy(() => import("../pages/services/erp/workday-support/page"));
const ERPOracleSupportPage = lazy(() => import("../pages/services/erp/oracle-support/page"));
const ERPPeopleSoftSupportPage = lazy(() => import("../pages/services/erp/peoplesoft-support/page"));
const ERPInforLawsonPage = lazy(() => import("../pages/services/erp/infor-lawson/page"));
const ERPInforCloudSuitePage = lazy(() => import("../pages/services/erp/infor-cloudsuite/page"));
const ERPUpgradesPage = lazy(() => import("../pages/services/erp/upgrades/page"));
const ERPIntegrationPage = lazy(() => import("../pages/services/erp/integration/page"));
const ERPTrainingPage = lazy(() => import("../pages/services/erp/training/page"));
const ERPConsultingPage = lazy(() => import("../pages/services/erp/consulting/page"));

// AI & Innovation Sub-service Pages
const AIStrategyPage = lazy(() => import("../pages/services/ai/strategy/page"));
const AIAgentsPage = lazy(() => import("../pages/services/ai/agents/page"));
const AutomationPage = lazy(() => import("../pages/services/ai/automation/page"));
const MachineLearningPage = lazy(() => import("../pages/services/ai/machine-learning/page"));
const GenerativeAIPage = lazy(() => import("../pages/services/ai/generative-ai/page"));
const AIAppDevelopmentPage = lazy(() => import("../pages/services/ai/app-development/page"));
const BusinessIntelligencePage = lazy(() => import("../pages/services/ai/business-intelligence/page"));

// Talent & Support Sub-service Pages
const ContractStaffingPage = lazy(() => import("../pages/services/staffing/contract/page"));
const DirectHirePage = lazy(() => import("../pages/services/staffing/direct-hire/page"));
const ManagedTeamsPage = lazy(() => import("../pages/services/staffing/managed-teams/page"));
const ExecutiveSearchPage = lazy(() => import("../pages/services/staffing/executive-search/page"));
const L1L2SupportPage = lazy(() => import("../pages/services/service-desk/l1-l2-support/page"));
const FieldSupportPage = lazy(() => import("../pages/services/service-desk/field-support/page"));

// Advisory & Strategy Sub-service Pages
const CloudMigrationPage = lazy(() => import("../pages/services/cloud/migration/page"));
const ManagedCloudPage = lazy(() => import("../pages/services/cloud/managed-cloud/page"));
const InfrastructureManagementPage = lazy(() => import("../pages/services/managed/infrastructure/page"));
const CybersecurityCompliancePage = lazy(() => import("../pages/services/managed/security/page"));
const ManagedPricingPage = lazy(() => import("../pages/services/managed/pricing/page"));
const ManagedPackagesPage = lazy(() => import("../pages/services/managed/packages/page"));
const LandingPage = lazy(() => import("../pages/landing/page"));
const ServiceLandingPage = lazy(() => import("../pages/landing/ServiceLandingPage"));
const ServicePricingPage = lazy(() => import("../pages/services/pricing/ServicePricingPage"));
const EHRSolutionsPage = lazy(() => import("../pages/services/healthcare-it/ehr/page"));
const HealthcareInteroperabilityPage = lazy(() => import("../pages/services/healthcare-it/interoperability/page"));

// Advisory & Strategy Dedicated Pages
const StrategyAdvisoryPage = lazy(() => import("../pages/services/strategy-advisory/page"));
const DigitalTransformationPage = lazy(() => import("../pages/services/digital-transformation/page"));
const TechAssessmentPage = lazy(() => import("../pages/services/tech-assessment/page"));
const ManagedCompliancePage = lazy(() => import("../pages/services/managed-compliance/page"));
const ChangeManagementPage = lazy(() => import("../pages/services/change-management/page"));

// Application & Dev Dedicated Pages
const AppFieldSupportPage = lazy(() => import("../pages/services/app/field-support/page"));
const AppDevelopmentPage = lazy(() => import("../pages/services/app/app-development/page"));
const DatabaseSupportPage = lazy(() => import("../pages/services/app/infrastructure/page"));
const VirtualCIOPage = lazy(() => import("../pages/services/app/virtual-cio/page"));
const DevelopmentServicesPage = lazy(() => import("../pages/services/app/development-services/page"));
const CloudServicesPage = lazy(() => import("../pages/services/app/cloud-services/page"));

const IndustriesPage = lazy(() => import("../pages/industries/page"));
const HealthcarePage = lazy(() => import("../pages/industries/healthcare/page"));
const GovernmentPage = lazy(() => import("../pages/industries/government/page"));
const CommercialPage = lazy(() => import("../pages/industries/commercial/page"));
const TransportationPage = lazy(() => import("../pages/industries/transportation/page"));
const EducationPage = lazy(() => import("../pages/industries/education/page"));
const EnergyPage = lazy(() => import("../pages/industries/energy/page"));
const ManufacturingPage = lazy(() => import("../pages/industries/manufacturing/page"));
const FinancialServicesPage = lazy(() => import("../pages/industries/financial-services/page"));
const RetailPage = lazy(() => import("../pages/industries/retail/page"));
const NonprofitPage = lazy(() => import("../pages/industries/nonprofit/page"));
const LegalPage = lazy(() => import("../pages/industries/legal/page"));
const HospitalityPage = lazy(() => import("../pages/industries/hospitality/page"));
const CareersPage = lazy(() => import("../pages/careers/page"));
const CareerJobPage = lazy(() => import("../pages/careers/job/page"));
const InsightsPage = lazy(() => import("../pages/insights/page"));
const ArticlePage = lazy(() => import("../pages/insights/article/page"));
const FormPage = lazy(() => import("../pages/forms/page"));
const NewsroomPage = lazy(() => import("../pages/newsroom/page"));
const NewsroomArticlePage = lazy(() => import("../pages/newsroom/article/page"));
const NewsroomCategoryPage = lazy(() => import("../pages/newsroom/category/page"));
const VideosPage = lazy(() => import("../pages/videos/page"));
const HowTosPage = lazy(() => import("../pages/videos/how-tos/page"));
const HowToCategoryPage = lazy(() => import("../pages/videos/how-tos/category/page"));
const ExplainersPage = lazy(() => import("../pages/videos/explainers/page"));
const TrainingVideosPage = lazy(() => import("../pages/videos/training/page"));
const CreatorsCutPage = lazy(() => import("../pages/creators-cut/page"));
const OriginalsPage = lazy(() => import("../pages/originals/page"));
const ShortsPage = lazy(() => import("../pages/videos/shorts/page"));
const LegacyTrainingRedirect = lazy(() => import("../pages/academy/redirects").then((m) => ({ default: m.LegacyTrainingRedirect })));
const AcademyHomePage = lazy(() => import("../pages/academy/home/page"));
const AcademyPage = lazy(() => import("../pages/academy/page"));
const AcademyCoursePage = lazy(() => import("../pages/academy/course/page"));
const AcademyLessonPage = lazy(() => import("../pages/academy/lesson/page"));
const AcademyPlatformPage = lazy(() => import("../pages/academy/platform/page"));
const AcademyPathsPage = lazy(() => import("../pages/academy/paths/page"));
const AcademyResourcesPage = lazy(() => import("../pages/academy/resources/page"));
const AcademyMyLearningPage = lazy(() => import("../pages/academy/my-learning/page"));
const AcademyAccessPage = lazy(() => import("../pages/academy/access/page"));
const AcademyTeamTrainingPage = lazy(() => import("../pages/academy/team-training/page"));
const AcademyCertificatePage = lazy(() => import("../pages/academy/certificates/page"));
const AcademyCourseRedirect = lazy(() => import("../pages/academy/redirects").then((m) => ({ default: m.AcademyCourseRedirect })));
const AcademyLessonRedirect = lazy(() => import("../pages/academy/redirects").then((m) => ({ default: m.AcademyLessonRedirect })));
const RequestConsultantsPage = lazy(() => import("../pages/request-consultants/page"));
const PartnershipsPage = lazy(() => import("../pages/partnerships/page"));
const InforPage = lazy(() => import("../pages/partnerships/infor/page"));
const MicrosoftPage = lazy(() => import("../pages/partnerships/microsoft/page"));
const AWSPage = lazy(() => import("../pages/partnerships/aws/page"));
const ConnectWisePage = lazy(() => import("../pages/partnerships/connectwise/page"));
const OraclePage = lazy(() => import("../pages/partnerships/oracle/page"));
const PrivacyPolicyPage = lazy(() => import("../pages/privacy-policy/page"));
const TermsOfServicePage = lazy(() => import("../pages/terms-of-service/page"));
const ResourcesPage = lazy(() => import("../pages/resources/page"));
const CaseStudiesPage = lazy(() => import("../pages/case-studies/page"));
const ResourceDetailPage = lazy(() => import("../pages/resources/article/page"));
const BlogPage = lazy(() => import("../pages/blog/page"));
const BlogPostPage = lazy(() => import("../pages/blog/post/page"));
const GovernmentContractingPage = lazy(() => import("../pages/government-contracting/page"));
const EngagementModelsPage = lazy(() => import("../pages/engagement-models/page"));
const GraphicPreviewPage = lazy(() => import("../pages/graphic-preview/page"));
const SearchPage = lazy(() => import("../pages/search/page"));
const LocationsDirectoryPage = lazy(() => import("../pages/locations/page"));
const CityHubPage = lazy(() => import("../pages/locations/hub/page"));
const CityServicePage = lazy(() => import("../pages/locations/service/page"));
const RegionServiceHubPage = lazy(() => import("../pages/locations/region/page"));
const RegionCountyPage = lazy(() => import("../pages/locations/county/page"));
const RegionIndustryPage = lazy(() => import("../pages/locations/industry/page"));
const RegionMetroPage = lazy(() => import("../pages/locations/metro/page"));
const NotFound = lazy(() => import("../pages/NotFound"));

// Admin imports
const AdminLayout = lazy(() => import("../pages/admin/components/AdminLayout"));
const ProtectedRoute = lazy(() => import("../pages/admin/components/ProtectedRoute"));
const AdminDashboard = lazy(() => import("../pages/admin/dashboard/page"));
const AdminPages = lazy(() => import("../pages/admin/pages/page"));
const AdminBrandKit = lazy(() => import("../pages/admin/brand-kit/page"));
const AdminServices = lazy(() => import("../pages/admin/services/page"));
const ServiceEditor = lazy(() => import("../pages/admin/services/editor/page"));
const AdminIndustries = lazy(() => import("../pages/admin/industries/page"));
const IndustryEditor = lazy(() => import("../pages/admin/industries/editor/page"));
const AdminInsights = lazy(() => import("../pages/admin/insights/page"));
const ArticleEditor = lazy(() => import("../pages/admin/insights/editor/page"));
const AdminResources = lazy(() => import("../pages/admin/resources/page"));
const ResourceEditor = lazy(() => import("../pages/admin/resources/editor/page"));
const AdminCaseStudies = lazy(() => import("../pages/admin/case-studies/page"));
const CaseStudyEditor = lazy(() => import("../pages/admin/case-studies/editor/page"));
const AdminBlog = lazy(() => import("../pages/admin/blog/page"));
const BlogEditor = lazy(() => import("../pages/admin/blog/editor/page"));
const AdminCareers = lazy(() => import("../pages/admin/careers/page"));
const CareerEditor = lazy(() => import("../pages/admin/careers/editor/page"));
const AdminSettings = lazy(() => import("../pages/admin/settings/page"));
const AdminUsers = lazy(() => import("../pages/admin/users/page"));
const AdminLogin = lazy(() => import("../pages/admin/login/page"));
const AdminAgency = lazy(() => import("../pages/admin/agency/page"));
const AdminTracking = lazy(() => import("../pages/admin/tracking/page"));
const AdminSearchVisibility = lazy(() => import("../pages/admin/marketing/search-visibility/page"));
const AdminSocialPublishing = lazy(() => import("../pages/admin/marketing/social-publishing/page"));
const AdminNewsroomStudio = lazy(() => import("../pages/admin/marketing/newsroom/page"));
const AdminNewsroomItem = lazy(() => import("../pages/admin/marketing/newsroom/item/page"));
const AdminContent  = lazy(() => import("../pages/admin/content/page"));
const AdminAcademy = lazy(() => import("../pages/admin/academy/page"));
const AdminAcademyEditor = lazy(() => import("../pages/admin/academy/editor/page"));
const AdminMediaLibrary = lazy(() => import("../pages/admin/content/media/page"));
const AdminCreatorsCut = lazy(() => import("../pages/admin/creators-cut/page"));
const AdminVideoLibrary = lazy(() => import("../pages/admin/content/videos/page"));
const AdminVideoCampaigns = lazy(() => import("../pages/admin/video-campaigns/page"));
const AdminClientLogos = lazy(() => import("../pages/admin/client-logos/page"));
const AdminSubmissions = lazy(() => import("../pages/admin/submissions/page"));
const AdminChats = lazy(() => import("../pages/admin/chats/page"));
const AdminReferenceResolver = lazy(() => import("../pages/admin/r/page"));
const AdminGrowthIntelligence = lazy(() => import("../pages/admin/marketing/growth/page"));
const AdminLocalPages = lazy(() => import("../pages/admin/marketing/local-pages/page"));
const AdminMcpAgents = lazy(() => import("../pages/admin/mcp/page"));

// OAuth 2.1 consent screen for MCP agent connections (admin-gated)
const OAuthAuthorizePage = lazy(() => import("../pages/oauth/authorize/page"));

/** Legacy /locations/* → /service-areas/* (client-side fallback for the edge 301). */
function LegacyLocationsRedirect() {
  const params = useParams();
  const splat = (params['*'] || '').replace(/^\/+/, '');
  return <Navigate to={splat ? `/service-areas/${splat}` : '/service-areas'} replace />;
}

const routes: RouteObject[] = [
  {
    path: "/",
    element: <HomePage />,
  },
  {
    path: "/contact",
    element: <ContactPage />,
  },
  {
    path: "/about",
    element: <AboutPage />,
  },
  {
    path: "/about/our-story",
    element: <OurStoryPage />,
  },
  {
    path: "/about/mission-values",
    element: <MissionValuesPage />,
  },
  {
    path: "/about/why-cendien",
    element: <WhyCendienPage />,
  },
  {
    path: "/about/certifications",
    element: <CertificationsAboutPage />,
  },
  {
    path: "/about/dallas-coverage",
    element: <DallasCoveragePage />,
  },
  {
    path: "/services",
    element: <ServicesPage />,
  },
  {
    path: "/services/staffing",
    element: <StaffingPage />,
  },
  {
    path: "/services/staffing/contract",
    element: <ContractStaffingPage />,
  },
  {
    path: "/services/staffing/direct-hire",
    element: <DirectHirePage />,
  },
  {
    path: "/services/staffing/managed-teams",
    element: <ManagedTeamsPage />,
  },
  {
    path: "/services/staffing/executive-search",
    element: <ExecutiveSearchPage />,
  },
  {
    path: "/services/erp",
    element: <ERPPage />,
  },
  {
    path: "/services/erp/implementation",
    element: <ERPImplementationPage />,
  },
  {
    path: "/services/erp/managed-services",
    element: <ERPManagedServicesPage />,
  },
  {
    path: "/services/erp/infor-support",
    element: <ERPInforSupportPage />,
  },
  {
    path: "/services/erp/workday-support",
    element: <ERPWorkdaySupportPage />,
  },
  {
    path: "/services/erp/oracle-support",
    element: <ERPOracleSupportPage />,
  },
  {
    path: "/services/erp/peoplesoft-support",
    element: <ERPPeopleSoftSupportPage />,
  },
  {
    path: "/services/erp/infor-lawson",
    element: <ERPInforLawsonPage />,
  },
  {
    path: "/services/erp/infor-cloudsuite",
    element: <ERPInforCloudSuitePage />,
  },
  {
    path: "/services/erp/upgrades",
    element: <ERPUpgradesPage />,
  },
  {
    path: "/services/erp/integration",
    element: <ERPIntegrationPage />,
  },
  {
    path: "/services/erp/training",
    element: <ERPTrainingPage />,
  },
  {
    path: "/services/erp/consulting",
    element: <ERPConsultingPage />,
  },
  {
    path: "/services/managed",
    element: <ManagedPage />,
  },
  {
    path: "/services/managed/infrastructure",
    element: <InfrastructureManagementPage />,
  },
  {
    path: "/services/managed/security",
    element: <CybersecurityCompliancePage />,
  },
  {
    path: "/services/managed/pricing",
    element: <ManagedPricingPage />,
  },
  {
    path: "/services/managed/packages",
    element: <ManagedPackagesPage />,
  },
  {
    path: "/managed-it/:slug",
    element: <LandingPage />,
  },
  { path: "/services/erp/infor-support/pricing", element: <ServicePricingPage lineId="infor" /> },
  { path: "/services/erp/workday-support/pricing", element: <ServicePricingPage lineId="workday" /> },
  { path: "/services/staffing/pricing", element: <ServicePricingPage lineId="staffing" /> },
  { path: "/services/ai/pricing", element: <ServicePricingPage lineId="ai" /> },
  { path: "/services/app/development-services/pricing", element: <ServicePricingPage lineId="dev" /> },
  { path: "/infor-support/:slug", element: <ServiceLandingPage lineId="infor" /> },
  { path: "/workday-support/:slug", element: <ServiceLandingPage lineId="workday" /> },
  { path: "/it-staffing/:slug", element: <ServiceLandingPage lineId="staffing" /> },
  { path: "/ai-services/:slug", element: <ServiceLandingPage lineId="ai" /> },
  { path: "/software-development/:slug", element: <ServiceLandingPage lineId="dev" /> },
  { path: "/infor-support", element: <Navigate to="/services/erp/infor-support/pricing" replace /> },
  { path: "/workday-support", element: <Navigate to="/services/erp/workday-support/pricing" replace /> },
  { path: "/it-staffing", element: <Navigate to="/services/staffing/pricing" replace /> },
  { path: "/ai-services", element: <Navigate to="/services/ai/pricing" replace /> },
  { path: "/software-development", element: <Navigate to="/services/app/development-services/pricing" replace /> },
  {
    path: "/managed-it",
    element: <Navigate to="/services/managed/packages" replace />,
  },
  {
    path: "/managed-it-packages",
    element: <Navigate to="/services/managed/packages" replace />,
  },
  {
    path: "/managed-it-pricing",
    element: <Navigate to="/services/managed/pricing" replace />,
  },
  {
    path: "/managed-it-services-cost-calculator",
    element: <Navigate to="/services/managed/pricing" replace />,
  },
  {
    path: "/services/service-desk",
    element: <ServiceDeskPage />,
  },
  {
    path: "/services/service-desk/l1-l2-support",
    element: <L1L2SupportPage />,
  },
  {
    path: "/services/service-desk/field-support",
    element: <FieldSupportPage />,
  },
  {
    path: "/services/ai",
    element: <AIPage />,
  },
  {
    path: "/services/ai/agents",
    element: <AIAgentsPage />,
  },
  {
    path: "/services/ai/strategy",
    element: <AIStrategyPage />,
  },
  {
    path: "/services/ai/automation",
    element: <AutomationPage />,
  },
  {
    path: "/services/ai/machine-learning",
    element: <MachineLearningPage />,
  },
  {
    path: "/services/ai/generative-ai",
    element: <GenerativeAIPage />,
  },
  {
    path: "/services/ai/app-development",
    element: <AIAppDevelopmentPage />,
  },
  {
    path: "/services/ai/business-intelligence",
    element: <BusinessIntelligencePage />,
  },
  {
    path: "/services/ai-data",
    element: <AIDataPage />,
  },
  {
    path: "/services/healthcare-it",
    element: <HealthcareITPage />,
  },
  {
    path: "/services/healthcare-it/ehr",
    element: <EHRSolutionsPage />,
  },
  {
    path: "/services/healthcare-it/interoperability",
    element: <HealthcareInteroperabilityPage />,
  },
  {
    path: "/services/cloud",
    element: <CloudPage />,
  },
  {
    path: "/services/cloud/migration",
    element: <CloudMigrationPage />,
  },
  {
    path: "/services/cloud/managed-cloud",
    element: <ManagedCloudPage />,
  },
  // ── Application & Dev Dedicated Pages ──────────────────────────────────────
  {
    path: "/services/app/field-support",
    element: <AppFieldSupportPage />,
  },
  {
    path: "/services/app/app-development",
    element: <AppDevelopmentPage />,
  },
  {
    path: "/services/app/infrastructure",
    element: <DatabaseSupportPage />,
  },
  {
    path: "/services/app/virtual-cio",
    element: <VirtualCIOPage />,
  },
  {
    path: "/services/app/development-services",
    element: <DevelopmentServicesPage />,
  },
  {
    path: "/services/app/cloud-services",
    element: <CloudServicesPage />,
  },
  // ── Advisory & Strategy Dedicated Pages ────────────────────────────────────
  {
    path: "/services/strategy-advisory",
    element: <StrategyAdvisoryPage />,
  },
  {
    path: "/services/digital-transformation",
    element: <DigitalTransformationPage />,
  },
  {
    path: "/services/tech-assessment",
    element: <TechAssessmentPage />,
  },
  {
    path: "/services/managed-compliance",
    element: <ManagedCompliancePage />,
  },
  {
    path: "/services/change-management",
    element: <ChangeManagementPage />,
  },
  {
    path: "/industries",
    element: <IndustriesPage />,
  },
  {
    path: "/industries/healthcare",
    element: <HealthcarePage />,
  },
  {
    path: "/industries/government",
    element: <GovernmentPage />,
  },
  {
    path: "/industries/commercial",
    element: <CommercialPage />,
  },
  {
    path: "/industries/transportation",
    element: <TransportationPage />,
  },
  {
    path: "/industries/education",
    element: <EducationPage />,
  },
  {
    path: "/industries/energy",
    element: <EnergyPage />,
  },
  {
    path: "/industries/manufacturing",
    element: <ManufacturingPage />,
  },
  {
    path: "/industries/financial-services",
    element: <FinancialServicesPage />,
  },
  {
    path: "/industries/retail",
    element: <RetailPage />,
  },
  {
    path: "/industries/nonprofit",
    element: <NonprofitPage />,
  },
  {
    path: "/industries/legal",
    element: <LegalPage />,
  },
  {
    path: "/industries/hospitality",
    element: <HospitalityPage />,
  },
  {
    path: "/careers",
    element: <CareersPage />,
  },
  {
    path: "/careers/:slug",
    element: <CareerJobPage />,
  },
  {
    path: "/insights",
    element: <InsightsPage />,
  },
  {
    path: "/insights/:slug",
    element: <ArticlePage />,
  },
  {
    // snip.re/domore was created pointing here before the assessment existed;
    // send it to the live managed services assessment.
    path: "/forms/do-more-with-less-white-paper",
    element: <Navigate to="/forms/managed-services-assessment" replace />,
  },
  {
    path: "/forms/:slug",
    element: <FormPage />,
  },
  {
    path: "/newsroom",
    element: <NewsroomPage />,
  },
  {
    path: "/newsroom/category/:slug",
    element: <NewsroomCategoryPage />,
  },
  {
    path: "/newsroom/:slug",
    element: <NewsroomArticlePage />,
  },
  {
    path: "/videos",
    element: <VideosPage />,
  },
  {
    path: "/videos/how-tos",
    element: <HowTosPage />,
  },
  {
    path: "/videos/how-tos/erp/:sub",
    element: <HowToCategoryPage />,
  },
  {
    path: "/videos/how-tos/:category",
    element: <HowToCategoryPage />,
  },
  {
    path: "/videos/shorts",
    element: <ShortsPage />,
  },
  {
    path: "/originals",
    element: <OriginalsPage />,
  },
  {
    path: "/originals/:seriesId",
    element: <OriginalsPage />,
  },
  {
    path: "/originals/:seriesId/:ep",
    element: <OriginalsPage />,
  },
  {
    path: "/videos/explainers",
    element: <ExplainersPage />,
  },
  {
    path: "/videos/training",
    element: <TrainingVideosPage />,
  },
  // Creators Cut — internal creative review room (Microsoft SSO + access code, no site chrome)
  {
    path: "/creators-cut",
    element: <CreatorsCutPage />,
  },
  // ── Cendien Academy (/academy/*) — canonical ──────────────────────────────
  {
    path: "/academy",
    element: <AcademyHomePage />,
  },
  {
    path: "/academy/catalog",
    element: <AcademyPage />,
  },
  {
    path: "/academy/platforms/:slug",
    element: <AcademyPlatformPage />,
  },
  {
    path: "/academy/courses/:courseSlug",
    element: <AcademyCoursePage />,
  },
  {
    path: "/academy/learn/:courseSlug/:lessonSlug",
    element: <AcademyLessonPage />,
  },
  {
    path: "/academy/paths",
    element: <AcademyPathsPage />,
  },
  {
    path: "/academy/resources",
    element: <AcademyResourcesPage />,
  },
  {
    path: "/academy/team-training",
    element: <AcademyTeamTrainingPage />,
  },
  {
    path: "/academy/my-learning",
    element: <AcademyMyLearningPage />,
  },
  {
    path: "/academy/access",
    element: <AcademyAccessPage />,
  },
  {
    path: "/academy/certificates/:publicId",
    element: <AcademyCertificatePage />,
  },
  // Pre-2024 /academy/course/... URLs still resolve.
  {
    path: "/academy/course/:courseSlug",
    element: <AcademyCourseRedirect />,
  },
  {
    path: "/academy/course/:courseSlug/:lessonSlug",
    element: <AcademyLessonRedirect />,
  },
  // Legacy /training/* (the academy's former home) → 301 into /academy/*.
  {
    path: "/training",
    element: <LegacyTrainingRedirect />,
  },
  {
    path: "/training/*",
    element: <LegacyTrainingRedirect />,
  },
  {
    path: "/request-consultants",
    element: <RequestConsultantsPage />,
  },
  {
    path: "/partnerships",
    element: <PartnershipsPage />,
  },
  {
    path: "/partnerships/infor",
    element: <InforPage />,
  },
  {
    path: "/partnerships/microsoft",
    element: <MicrosoftPage />,
  },
  {
    path: "/partnerships/aws",
    element: <AWSPage />,
  },
  {
    path: "/partnerships/connectwise",
    element: <ConnectWisePage />,
  },
  {
    path: "/partnerships/workday",
    element: <Navigate to="/services/erp/workday-support" replace />,
  },
  {
    path: "/partnerships/oracle",
    element: <OraclePage />,
  },
  {
    path: "/resources",
    element: <ResourcesPage />,
  },
  {
    path: "/case-studies",
    element: <CaseStudiesPage />,
  },
  {
    path: "/government-contracting",
    element: <GovernmentContractingPage />,
  },
  {
    path: "/engagement-models",
    element: <EngagementModelsPage />,
  },
  {
    path: "/resources/:slug",
    element: <ResourceDetailPage />,
  },
  {
    path: "/graphic-preview/:shareId",
    element: <GraphicPreviewPage />,
  },
  {
    path: "/search",
    element: <SearchPage />,
  },
  // ── Local Service Pages (/service-areas/*) — never linked from the home page
  //    or the main nav; reached from the footer "Service areas" link, the
  //    sitemap and llms.txt. Legacy /locations/* URLs 301 to here at the edge. ──
  {
    path: "/service-areas",
    element: <LocationsDirectoryPage />,
  },
  // Non-city page types — reserved prefixes so they never collide with a city
  // hub (/service-areas/{city}) or city-service (/service-areas/{city}/{service}).
  {
    path: "/service-areas/region/:serviceSlug",
    element: <RegionServiceHubPage />,
  },
  {
    path: "/service-areas/county/:countySlug",
    element: <RegionCountyPage />,
  },
  {
    path: "/service-areas/industry/:industrySlug/:citySlug",
    element: <RegionIndustryPage />,
  },
  {
    path: "/service-areas/metro/:regionSlug",
    element: <RegionMetroPage />,
  },
  {
    path: "/service-areas/:citySlug/:serviceSlug",
    element: <CityServicePage />,
  },
  {
    path: "/service-areas/:citySlug",
    element: <CityHubPage />,
  },
  // Legacy /locations/* — client-side fallback for any in-app link that still
  // points at the old scheme (the edge 301 handles full page loads).
  {
    path: "/locations/*",
    element: <LegacyLocationsRedirect />,
  },
  {
    path: "/blog",
    element: <BlogPage />,
  },
  {
    path: "/blog/:slug",
    element: <BlogPostPage />,
  },
  {
    path: "/privacy-policy",
    element: <PrivacyPolicyPage />,
  },
  {
    path: "/terms-of-service",
    element: <TermsOfServicePage />,
  },
  // OAuth 2.1 consent screen — requires the normal admin sign-in
  {
    path: '/oauth/authorize',
    element: (
      <ProtectedRoute>
        <OAuthAuthorizePage />
      </ProtectedRoute>
    ),
  },
  // Admin Routes
  {
    path: '/admin/login',
    element: <AdminLogin />,
  },
  {
    path: '/admin',
    element: (
      <ProtectedRoute>
        <AdminLayout>
          <Outlet />
        </AdminLayout>
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: <AdminDashboard /> },
      { path: 'dashboard', element: <AdminDashboard /> },
      { path: 'pages', element: <AdminPages /> },
      { path: 'brand-kit', element: <AdminBrandKit /> },
      { path: 'client-logos', element: <AdminClientLogos /> },
      { path: 'services', element: <AdminServices /> },
      { path: 'services/:serviceId', element: <ServiceEditor /> },
      { path: 'industries', element: <AdminIndustries /> },
      { path: 'industries/:industryId', element: <IndustryEditor /> },
      { path: 'insights', element: <AdminInsights /> },
      { path: 'insights/:articleId', element: <ArticleEditor /> },
      { path: 'resources', element: <AdminResources /> },
      { path: 'resources/:resourceId', element: <ResourceEditor /> },
      { path: 'case-studies', element: <AdminCaseStudies /> },
      { path: 'case-studies/:caseStudyId', element: <CaseStudyEditor /> },
      { path: 'blog', element: <AdminBlog /> },
      { path: 'blog/:blogId', element: <BlogEditor /> },
      { path: 'careers', element: <AdminCareers /> },
      { path: 'careers/:careerId', element: <CareerEditor /> },
      { path: 'users', element: <AdminUsers /> },
      { path: 'settings', element: <AdminSettings /> },
      { path: 'mcp', element: <AdminMcpAgents /> },
      { path: 'content', element: <AdminContent /> },
      { path: 'academy', element: <AdminAcademy /> },
      { path: 'academy/access', element: <Navigate to="/admin/academy?tab=access" replace /> },
      { path: 'academy/:courseId', element: <AdminAcademyEditor /> },
      { path: 'content/media', element: <AdminMediaLibrary /> },
      { path: 'creators-cut', element: <AdminCreatorsCut /> },
      { path: 'videos', element: <AdminVideoLibrary /> },
      { path: 'content/videos', element: <AdminVideoLibrary /> },
      { path: 'video-campaigns', element: <AdminVideoCampaigns /> },
      { path: 'form-submissions', element: <AdminSubmissions /> },
      { path: 'submissions', element: <AdminSubmissions /> },
      { path: 'chats', element: <AdminChats /> },
      { path: 'chat-settings', element: <Navigate to="/admin/settings?tab=chat-assistant" replace /> },
      { path: 'r/:ref', element: <AdminReferenceResolver /> },
      { path: 'agency', element: <AdminAgency /> },
      { path: 'tracking', element: <AdminTracking /> },
      { path: 'marketing/search-visibility', element: <AdminSearchVisibility /> },
      { path: 'marketing/growth', element: <AdminGrowthIntelligence /> },
      { path: 'marketing/local-pages', element: <AdminLocalPages /> },
      { path: 'marketing/social-publishing', element: <AdminSocialPublishing /> },
      { path: 'marketing/newsroom', element: <AdminNewsroomStudio /> },
      { path: 'marketing/newsroom/:id', element: <AdminNewsroomItem /> },
    ],
  },
  {
    path: "*",
    element: <NotFound />,
  },
];

export default routes;